import { Flex, Text, IconButton, Spinner } from '@radix-ui/themes'
import {
  LockClosedIcon,
  LockOpen1Icon,
  Pencil2Icon,
  CheckIcon,
  Cross2Icon,
} from '@radix-ui/react-icons'
import { ReactNode, JSX } from 'react'

import { SectionState } from './claim-form-constants'

type ClaimFormSectionProps = {
  title: string
  icon: JSX.Element
  sectionState: SectionState
  onToggleLock: () => void
  onStartEditing: () => void
  onSave: () => void
  onCancelEdit: () => void
  isSaving?: boolean
  lockedHint: string
  children: ReactNode
  editChildren?: ReactNode
  saveError?: string
}

export const ClaimFormSection = ({
  title,
  icon,
  sectionState,
  onToggleLock,
  onStartEditing,
  onSave,
  onCancelEdit,
  isSaving,
  lockedHint,
  children,
  editChildren,
  saveError,
}: ClaimFormSectionProps) => {
  const isLocked = sectionState === SectionState.LOCKED
  const isEditing = sectionState === SectionState.EDITING

  const handleHeaderClick = () => {
    if (!isEditing) {
      onToggleLock()
    }
  }

  return (
    <div className="claim-form-section">
      <Flex justify="between" align="center" className="claim-form-section-header">
        <Flex
          align="center"
          gap="2"
          onClick={handleHeaderClick}
          style={{ cursor: isEditing ? 'default' : 'pointer', flex: 1 }}>
          {icon}
          <Text weight="bold" size="3">
            {title}
          </Text>
        </Flex>

        <Flex align="center" gap="1">
          {isEditing ? (
            <>
              <IconButton
                size="1"
                variant="ghost"
                color="green"
                onClick={onSave}
                disabled={isSaving}
                aria-label="Save changes">
                {isSaving ? <Spinner size="1" /> : <CheckIcon />}
              </IconButton>
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                onClick={onCancelEdit}
                disabled={isSaving}
                aria-label="Cancel editing">
                <Cross2Icon />
              </IconButton>
            </>
          ) : !isLocked ? (
            <>
              {editChildren !== undefined && (
                <IconButton
                  size="1"
                  variant="ghost"
                  color="gray"
                  onClick={onStartEditing}
                  aria-label="Edit section">
                  <Pencil2Icon />
                </IconButton>
              )}
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                onClick={onToggleLock}
                aria-label="Lock section">
                <LockOpen1Icon className="claim-form-lock-icon" />
              </IconButton>
            </>
          ) : (
            <LockClosedIcon
              className="claim-form-lock-icon"
              style={{ cursor: 'pointer' }}
              onClick={onToggleLock}
            />
          )}
        </Flex>
      </Flex>

      {isLocked ? (
        <div className="claim-form-locked-placeholder" onClick={onToggleLock}>
          <div className="claim-form-blur-bar" />
          <div className="claim-form-blur-bar short" />
          <div className="claim-form-blur-bar" />
          <Text size="1" color="gray" className="claim-form-locked-hint">
            {lockedHint}
          </Text>
        </div>
      ) : isEditing && editChildren ? (
        <div className="claim-form-section-content revealed">
          <div className="claim-form-section-fields">{editChildren}</div>
          {saveError && (
            <Text size="1" color="red" style={{ paddingTop: '4px' }}>
              {saveError}
            </Text>
          )}
        </div>
      ) : (
        <div className="claim-form-section-content revealed">
          <div className="claim-form-section-fields">{children}</div>
        </div>
      )}
    </div>
  )
}
